import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import NotificationCenter from '../components/NotificationCenter';
import MockPaymentDialog from '../components/MockPaymentDialog';
import StudentChat from '../components/StudentChat';
import StudentWallet from '../components/StudentWallet';
import FloatingChat from '../components/FloatingChat';
import { useMockBank } from '../lib/mockBank';
import {
  GraduationCap,
  Clock,
  FileText,
  Play,
  CheckCircle,
  LogOut,
  Loader2,
  BookOpen,
  AlertTriangle,
  Video,
  PlayCircle,
  Users,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  ChevronLeft,
  Sparkles,
  Calendar,
  User,
  Send,
  Layers,
  RefreshCw,
  Folder,
  FolderOpen,
  AlertCircle,
  Lock,
  CreditCard,
  Wallet,
  PlayCircle as PlayCircleIcon
} from 'lucide-react';
import { toast } from 'sonner';

const StudentExamList = () => {
  const navigate = useNavigate();
  const { student, token, logout, isStudent, isApprovedStudent } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = ['exams', 'topics', 'videos', 'balance', 'dashboard'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'dashboard';
  const [exams, setExams] = useState([]);
  const [completedExams, setCompletedExams] = useState([]);
  const [topics, setTopics] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(initialTab);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  // Folder being checked out in the mock payment dialog (null = closed)
  const [payTopic, setPayTopic] = useState(null);
  const [chatUnread, setChatUnread] = useState(0);
  const [balance, setBalance] = useState(0);
  // Recharge-card code tapped from the chat → switch to wallet and auto-fill it.
  const [redeemPrefill, setRedeemPrefill] = useState(null);

  const handleRedeemFromChat = (code) => {
    if (!code) return;
    // Bump with a fresh object so re-tapping the SAME code re-triggers the prefill.
    setRedeemPrefill({ code, ts: Date.now() });
    setActiveSection('balance');
    toast.success('تم نسخ كود البطاقة إلى «رصيد التعلّم» — اضغط «شحن الرصيد»');
  };

  // Mock paid question-bank store (front-end only, no real payment)
  const bank = useMockBank();
  const studentId = student?.id;

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Dashboard sections configuration
  const dashboardSections = [
    { id: 'dashboard', label: 'الرئيسية', icon: GraduationCap },
    // { id: 'interactive-lessons', label: 'الدروس التفاعلية', icon: Sparkles },
    // { id: 'recorded-lessons', label: 'الدروس المسجلة', icon: Video },
    // { id: 'private-lessons', label: 'دروس خاصة', icon: Users },
    { id: 'exams', label: 'الاختبارات', icon: FileText },
    { id: 'topics', label: 'بنك الأسئلة', icon: Layers },
    { id: 'videos', label: 'الدروس المسجلة', icon: Video },
    { id: 'balance', label: 'رصيد التعلّم', icon: Wallet },
    // { id: 'contact', label: 'تواصل معنا', icon: MessageCircle },
  ];

  // Sample interactive lessons data (can be fetched from API later)
  const interactiveLessons = [
    { id: 1, title: 'درس النحو التفاعلي - المبتدأ والخبر', duration: '45 دقيقة', subject: 'اللغة العربية', status: 'available' },
    { id: 2, title: 'درس الإملاء - الهمزة المتوسطة', duration: '30 دقيقة', subject: 'اللغة العربية', status: 'available' },
    { id: 3, title: 'درس البلاغة - التشبيه والاستعارة', duration: '40 دقيقة', subject: 'اللغة العربية', status: 'coming_soon' },
  ];

  // Sample recorded lessons data
  const recordedLessons = [
    { id: 1, title: 'شرح قواعد النحو الأساسية', duration: '1:20:00', subject: 'اللغة العربية', views: 234, date: '2026-01-15' },
    { id: 2, title: 'تحليل النصوص الأدبية', duration: '55:00', subject: 'اللغة العربية', views: 189, date: '2026-01-20' },
    { id: 3, title: 'مهارات الكتابة الإبداعية', duration: '1:05:00', subject: 'اللغة العربية', views: 156, date: '2026-02-01' },
    { id: 4, title: 'الإعراب والتطبيقات', duration: '1:30:00', subject: 'اللغة العربية', views: 312, date: '2026-02-05' },
  ];

  useEffect(() => {
    if (!isStudent) {
      navigate('/student/login');
      return;
    }

    if (!isApprovedStudent) {
      navigate('/student/pending');
      return;
    }

    fetchExams();
    fetchNews();
  }, [isStudent, isApprovedStudent, navigate]);

  // Presence heartbeat + support-chat unread badge polling
  useEffect(() => {
    if (!isApprovedStudent || !token) return;
    const ping = async () => {
      try {
        await fetch(`${API_URL}/api/student/heartbeat`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
      } catch { /* ignore */ }
    };
    const loadUnread = async () => {
      try {
        const r = await fetch(`${API_URL}/api/student/messages/unread`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setChatUnread((await r.json()).unread || 0);
      } catch { /* ignore */ }
    };
    const loadBalance = async () => {
      try {
        const r = await fetch(`${API_URL}/api/student/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setBalance((await r.json()).balance || 0);
      } catch { /* ignore */ }
    };
    ping();
    loadUnread();
    loadBalance();
    const t1 = setInterval(ping, 30000);
    const t2 = setInterval(loadUnread, 8000);
    const t3 = setInterval(loadBalance, 10000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); };
  }, [isApprovedStudent, token, API_URL]);

  const fetchNews = async () => {
    try {
      const r = await fetch(`${API_URL}/api/student/news`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) setNewsItems(await r.json());
    } catch { /* ignore */ }
  };

  const refreshTopics = async () => {
    try {
      const r = await fetch(`${API_URL}/api/student/topics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setTopics(await r.json());
    } catch { /* ignore */ }
  };

  const refreshBalance = async () => {
    try {
      const r = await fetch(`${API_URL}/api/student/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setBalance((await r.json()).balance || 0);
    } catch { /* ignore */ }
  };

  // Refresh topic access + balance when returning to the tab/window so a topic
  // that got re-locked (old-question limit reached) shows as locked immediately.
  useEffect(() => {
    if (!isApprovedStudent || !token) return undefined;
    const onFocus = () => { refreshTopics(); refreshBalance(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApprovedStudent, token]);

  useEffect(() => {
    if (activeSection === 'topics' && isApprovedStudent) { refreshTopics(); refreshBalance(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const refreshVideos = async () => {
    try {
      const r = await fetch(`${API_URL}/api/student/videos`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setVideos(await r.json());
    } catch { /* ignore */ }
  };

  // Unlock a WHOLE video folder by paying its price from the wallet.
  const unlockVideoFolder = async (category, teacherId) => {
    try {
      const r = await fetch(`${API_URL}/api/student/folders/unlock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: category || '', teacher_id: teacherId || null, scope: 'video' }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(data.message || 'تم فتح المجلد');
        await Promise.all([refreshVideos(), refreshBalance()]);
      } else if (r.status === 402) {
        toast.error(data.detail || 'رصيدك غير كافٍ — اشحن رصيدك أولاً');
        setActiveSection('balance');
      } else {
        toast.error(data.detail || 'تعذّر فتح المجلد');
      }
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  // Unlock a WHOLE folder by paying its price from the wallet (#7, folder-level).
  const unlockFolder = async (category, teacherId) => {
    try {
      const r = await fetch(`${API_URL}/api/student/folders/unlock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: category || '', teacher_id: teacherId || null }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(data.message || 'تم فتح المجلد');
        await Promise.all([refreshTopics(), refreshBalance()]);
      } else if (r.status === 402) {
        toast.error(data.detail || 'رصيدك غير كافٍ — اشحن رصيدك أولاً');
        setActiveSection('balance');
      } else {
        toast.error(data.detail || 'تعذّر فتح المجلد');
      }
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const fetchExams = async () => {
    setLoading(true);
    try {
      // Fetch available exams
      const response = await fetch(`${API_URL}/api/student/exams`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setExams(data);
      } else if (response.status === 403) {
        // Not approved
        navigate('/student/pending');
        return;
      }

      // Fetch student's completed exams with authorization
      const resultsResponse = await fetch(`${API_URL}/api/results/student/${student.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (resultsResponse.ok) {
        const resultsData = await resultsResponse.json();
        setCompletedExams(resultsData);
      }

      // Fetch topics (question bank) for this student's grade
      const topicsResponse = await fetch(`${API_URL}/api/student/topics`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (topicsResponse.ok) {
        const topicsData = await topicsResponse.json();
        setTopics(topicsData);
      }

      // Fetch recorded videos for this student's grade
      const videosResponse = await fetch(`${API_URL}/api/student/videos`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (videosResponse.ok) {
        setVideos(await videosResponse.json());
      }
    } catch (error) {
      console.error('Error fetching exams:', error);
      toast.error('حدث خطأ أثناء تحميل الاختبارات');
    }
    setLoading(false);
  };

  const handleStartExam = (examId) => {
    navigate(`/student/exam/${examId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('يرجى ملء جميع الحقول');
      return;
    }
    
    setSendingMessage(true);
    // Simulate sending message (can be connected to API later)
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('تم إرسال رسالتك بنجاح! سنتواصل معك قريباً');
    setContactForm({ name: '', email: '', message: '' });
    setSendingMessage(false);
  };

  const isExamCompleted = (examId) => {
    return completedExams.some(e => e.session?.exam_id === examId);
  };

  const getExamResult = (examId) => {
    return completedExams.find(e => e.session?.exam_id === examId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#EF9F27] mx-auto mb-4" />
          <p className="text-[#412402]/60">جاري تحميل الاختبارات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FFFFFF] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#EF9F27]">المعتمد في اللغة العربية</span>
            </Link>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveSection('balance')}
                className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 rounded-full px-3 py-1.5 font-bold text-sm transition-colors"
                title="رصيد التعلّم"
                data-testid="header-balance"
              >
                <Wallet className="w-4 h-4 text-amber-700" />
                {balance} <span className="font-normal text-amber-700/70 text-xs">د.أ</span>
              </button>
              <NotificationCenter />
              <div className="text-left hidden sm:block">
                <p className="text-sm font-medium text-[#412402]">{student?.name || student?.full_name}</p>
                <p className="text-xs text-[#412402]/50">{student?.grade} - شعبة {student?.section}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleLogout}
                className="border-[#E8DFCF] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 ml-2" />
                خروج
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* News Ticker */}
      {newsItems.length > 0 && <NewsTicker items={newsItems} />}

      {/* Navigation Tabs */}
      <nav className="bg-[#FFFFFF]/80 border-b border-[#E8DFCF] overflow-x-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 py-2">
            {dashboardSections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    activeSection === section.id
                      ? 'bg-[#EF9F27]/15 text-[#EF9F27]'
                      : 'text-[#412402]/60 hover:bg-[#FFFFFF]/50'
                  }`}
                  data-testid={`nav-${section.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {section.label}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Dashboard Section */}
        {activeSection === 'dashboard' && (
          <DashboardHome
            student={student}
            exams={exams}
            completedExams={completedExams}
            interactiveLessons={interactiveLessons}
            recordedLessons={recordedLessons}
            setActiveSection={setActiveSection}
            balance={balance}
          />
        )}

        {/* Interactive Lessons Section */}
        {activeSection === 'interactive-lessons' && (
          <InteractiveLessonsSection lessons={interactiveLessons} />
        )}

        {/* Recorded Lessons Section */}
        {activeSection === 'recorded-lessons' && (
          <RecordedLessonsSection lessons={recordedLessons} />
        )}

        {/* Private Lessons Section */}
        {activeSection === 'private-lessons' && (
          <PrivateLessonsSection />
        )}

        {/* Exams Section */}
        {activeSection === 'exams' && (
          <ExamsSection
            exams={exams}
            completedExams={completedExams}
            handleStartExam={handleStartExam}
          />
        )}

        {/* Topics (Question Bank) Section */}
        {activeSection === 'topics' && (
          <TopicsSection
            topics={topics}
            navigate={navigate}
            bank={bank}
            studentId={studentId}
            onPay={(topic) => setPayTopic(topic)}
            onUnlock={unlockFolder}
          />
        )}

        {/* Recorded Videos Section */}
        {activeSection === 'videos' && (
          <VideosSection
            videos={videos}
            token={token}
            apiUrl={API_URL}
            onUnlockFolder={unlockVideoFolder}
          />
        )}

        {/* Learning Wallet (balance) Section */}
        {activeSection === 'balance' && (
          <StudentWallet apiUrl={API_URL} token={token} prefillCode={redeemPrefill?.code} />
        )}


        {/* Contact Section */}
        {activeSection === 'contact' && (
          <ContactSection 
            contactForm={contactForm}
            setContactForm={setContactForm}
            handleContactSubmit={handleContactSubmit}
            sendingMessage={sendingMessage}
          />
        )}

      </main>

      {/* Mock checkout — pay the full price of one folder (no real payment) */}
      <MockPaymentDialog
        open={!!payTopic}
        onOpenChange={(v) => { if (!v) setPayTopic(null); }}
        topicTitle={payTopic?.title || ''}
        price={payTopic ? bank.getPrice(payTopic.id) : 0}
        onConfirm={() => { if (payTopic) bank.purchaseTopic(studentId, payTopic.id); }}
      />

      {/* Floating support chat (replaces the old "محادثة المعلم" tab) */}
      <FloatingChat unread={chatUnread} title="الدعم الفني">
        <StudentChat apiUrl={API_URL} token={token} compact onRedeemCode={handleRedeemFromChat} />
      </FloatingChat>
    </div>
  );
};

// ============== NEWS TICKER COMPONENT ==============
const NewsTicker = ({ items }) => {
  const activeItems = items.filter(i => i.is_active);
  if (!activeItems.length) return null;

  const text = activeItems.map(i => i.content).join('     ◆     ');
  const duration = Math.max(18, Math.round(text.length * 0.13));

  return (
    <div style={{
      background: 'linear-gradient(90deg, #1a0500 0%, #2d0e00 100%)',
      borderBottom: '2px solid #EF9F27',
      height: '44px',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      // Must NOT be rtl so the label stays on the left side
      direction: 'ltr',
    }}>

      {/* Label badge — empty colored box (no text, no icon) */}
      <div style={{
        flexShrink: 0,
        background: 'linear-gradient(135deg, #EF9F27 0%, #D88E1F 100%)',
        width: '16px',
        height: '44px',
        boxShadow: '4px 0 12px rgba(0,0,0,0.5)',
        zIndex: 2,
      }} />

      {/*
        Scrolling area — direction: ltr is CRITICAL.
        The page is RTL, which reverses inline-flex order and
        breaks translateX(-50%) so the text never enters the viewport.
        Forcing ltr here makes span1 start at x=0 (visible),
        scrolls left, span2 fills seamlessly → infinite loop.
      */}
      <div style={{
        flex: 1,
        overflow: 'hidden',
        direction: 'ltr',
        height: '44px',
        display: 'flex',
        alignItems: 'center',
      }}>
        <div style={{
          display: 'inline-flex',
          direction: 'ltr',
          whiteSpace: 'nowrap',
          animation: `__ticker__ ${duration}s linear infinite`,
          willChange: 'transform',
        }}>
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: '#FFF8E7',
            paddingRight: '80px',
          }}>
            {text}
          </span>
          <span style={{
            fontSize: '15px',
            fontWeight: '600',
            color: '#FFF8E7',
            paddingRight: '80px',
          }}>
            {text}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes __ticker__ {
          0%   { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

// ============== DASHBOARD HOME COMPONENT ==============
const DashboardHome = ({ student, exams, completedExams, interactiveLessons, recordedLessons, setActiveSection, balance = 0 }) => {
  const completedCount = completedExams.length;
  const availableExamsCount = exams.length;
  const totalScore = completedExams.reduce((sum, r) => sum + (r.session?.score || 0), 0);
  const totalPossible = completedExams.reduce((sum, r) => sum + (r.total_points || 0), 0);

  return (
    <div className="space-y-8 bg-[#FFFFFF]">
      {/* Welcome Section */}
      <div className="bg-gradient-to-l from-[#EF9F27] to-[#D88E1F] rounded-2xl p-6 text-[#412402] flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold mb-2">أهلًا {student?.name || student?.full_name}</h1>
          <p className="text-[#412402]">استمر في التعلم وحقق أهدافك الدراسية</p>
        </div>
        <button
          onClick={() => setActiveSection('balance')}
          className="bg-white/90 hover:bg-white rounded-2xl px-5 py-3 text-center shadow-sm transition-colors"
          data-testid="dashboard-balance"
        >
          <div className="flex items-center gap-2 justify-center text-amber-800">
            <Wallet className="w-5 h-5" />
            <span className="text-xs font-medium">رصيد التعلّم</span>
          </div>
          <p className="text-2xl font-bold text-[#412402] mt-1">{balance} <span className="text-sm font-normal"></span></p>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
        <Card className="border-[#E8DFCF]">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-[#412402]">{availableExamsCount}</p>
            <p className="text-sm text-[#412402]/50">اختبارات متاحة</p>
          </CardContent>
        </Card>
        
        <Card className="border-[#E8DFCF]">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-[#412402]">{completedCount}</p>
            <p className="text-sm text-[#412402]/50">اختبارات مكتملة</p>
          </CardContent>
        </Card>
        
        {/* <Card className="border-[#E8DFCF]">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Video className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-[#412402]">{recordedLessons.length}</p>
            <p className="text-sm text-[#412402]/50">دروس مسجلة</p>
          </CardContent>
        </Card> */}
        
        {/* <Card className="border-[#E8DFCF]">
          <CardContent className="p-4 text-center">
            <div className="w-12 h-12 bg-[#EF9F27]/15 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Sparkles className="w-6 h-6 text-[#EF9F27]" />
            </div>
            <p className="text-2xl font-bold text-[#412402]">{totalPossible > 0 ? Math.round((totalScore / totalPossible) * 100) : 0}%</p>
            <p className="text-sm text-[#412402]/50">معدل النجاح</p>
          </CardContent>
        </Card> */}
      </div>

      {/* Quick Access Cards */}
      <div className="grid md:grid-cols-1 lg:grid-cols-1 gap-6">
        {/* Interactive Lessons Preview */}
        {/* <Card className="border-[#E8DFCF] hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection('interactive-lessons')}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                الدروس التفاعلية
              </CardTitle>
              <ChevronLeft className="w-5 h-5 text-[#412402]/40" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#412402]/60 mb-3">{interactiveLessons.filter(l => l.status === 'available').length} درس متاح</p>
            <div className="space-y-2">
              {interactiveLessons.slice(0, 2).map(lesson => (
                <div key={lesson.id} className="flex items-center justify-between text-sm">
                  <span className="text-[#412402]/80 truncate">{lesson.title}</span>
                  <Badge variant={lesson.status === 'available' ? 'default' : 'secondary'} className="text-xs">
                    {lesson.status === 'available' ? 'متاح' : 'قريباً'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card> */}

        {/* Recorded Lessons Preview */}
        {/* <Card className="border-[#E8DFCF] hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection('recorded-lessons')}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Video className="w-5 h-5 text-purple-500" />
                الدروس المسجلة
              </CardTitle>
              <ChevronLeft className="w-5 h-5 text-[#412402]/40" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#412402]/60 mb-3">{recordedLessons.length} درس مسجل</p>
            <div className="space-y-2">
              {recordedLessons.slice(0, 2).map(lesson => (
                <div key={lesson.id} className="flex items-center justify-between text-sm">
                  <span className="text-[#412402]/80 truncate">{lesson.title}</span>
                  <span className="text-[#412402]/50">{lesson.duration}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card> */}

        {/* Exams Preview */}
        <Card className="border-[#E8DFCF] hover:shadow-md transition-shadow cursor-pointer" onClick={() => setActiveSection('exams')}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-500" />
                الاختبارات
              </CardTitle>
              <ChevronLeft className="w-5 h-5 text-[#412402]/40" />
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#412402]/60 mb-3">{exams.length} اختبار متاح</p>
            <div className="space-y-2">
              {exams.slice(0, 2).map(exam => (
                <div key={exam.id} className="flex items-center justify-between text-sm">
                  <span className="text-[#412402]/80 truncate">{exam.title}</span>
                  <span className="text-[#412402]/50">{exam.duration_minutes} دقيقة</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== INTERACTIVE LESSONS SECTION ==============
const InteractiveLessonsSection = ({ lessons }) => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#412402]">الدروس التفاعلية</h1>
        <p className="text-[#412402]/60 mt-1">تعلم بطريقة تفاعلية وممتعة</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {lessons.map((lesson) => (
          <Card key={lesson.id} className="border-[#E8DFCF] overflow-hidden hover:shadow-lg transition-shadow">
            <div className="h-32 bg-gradient-to-l from-amber-400 to-orange-500 flex items-center justify-center">
              <Sparkles className="w-16 h-16 text-[#412402]/80" />
            </div>
            <CardContent className="p-4">
              <Badge variant={lesson.status === 'available' ? 'default' : 'secondary'} className="mb-2">
                {lesson.status === 'available' ? 'متاح الآن' : 'قريباً'}
              </Badge>
              <h3 className="font-semibold text-[#412402] mb-2">{lesson.title}</h3>
              <div className="flex items-center gap-4 text-sm text-[#412402]/50 mb-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {lesson.duration}
                </span>
                <span className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  {lesson.subject}
                </span>
              </div>
              <Button 
                className="w-full" 
                variant={lesson.status === 'available' ? 'default' : 'secondary'}
                disabled={lesson.status !== 'available'}
                data-testid={`start-interactive-lesson-${lesson.id}`}
              >
                {lesson.status === 'available' ? (
                  <>
                    <Play className="w-4 h-4 ml-2" />
                    ابدأ الدرس
                  </>
                ) : (
                  'قريباً'
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {lessons.length === 0 && (
        <Card className="border-[#E8DFCF]">
          <CardContent className="py-12 text-center">
            <Sparkles className="w-16 h-16 text-[#412402]/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#412402]/80 mb-2">لا توجد دروس تفاعلية حالياً</h3>
            <p className="text-[#412402]/50">سيتم إضافة دروس جديدة قريباً</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== RECORDED LESSONS SECTION ==============
const RecordedLessonsSection = ({ lessons }) => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#412402]">الدروس المسجلة</h1>
        <p className="text-[#412402]/60 mt-1">شاهد الدروس في أي وقت يناسبك</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {lessons.map((lesson) => (
          <Card key={lesson.id} className="border-[#E8DFCF] overflow-hidden hover:shadow-lg transition-shadow">
            <div className="flex">
              <div className="w-48 h-32 bg-gradient-to-l from-purple-500 to-indigo-600 flex items-center justify-center shrink-0">
                <PlayCircle className="w-12 h-12 text-[#412402]/80" />
              </div>
              <CardContent className="p-4 flex-1">
                <h3 className="font-semibold text-[#412402] mb-2 line-clamp-2">{lesson.title}</h3>
                <div className="space-y-1 text-sm text-[#412402]/50 mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{lesson.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(lesson.date).toLocaleDateString('ar-SA')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{lesson.views} مشاهدة</span>
                  </div>
                </div>
                <Button size="sm" className="w-full" data-testid={`watch-lesson-${lesson.id}`}>
                  <Play className="w-4 h-4 ml-2" />
                  مشاهدة
                </Button>
              </CardContent>
            </div>
          </Card>
        ))}
      </div>

      {lessons.length === 0 && (
        <Card className="border-[#E8DFCF]">
          <CardContent className="py-12 text-center">
            <Video className="w-16 h-16 text-[#412402]/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#412402]/80 mb-2">لا توجد دروس مسجلة حالياً</h3>
            <p className="text-[#412402]/50">سيتم إضافة دروس جديدة قريباً</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== PRIVATE LESSONS SECTION ==============
const PrivateLessonsSection = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#412402]">دروس خاصة عبر الإنترنت</h1>
        <p className="text-[#412402]/60 mt-1">احصل على دروس فردية مع أفضل المعلمين</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Info Card */}
        <Card className="border-[#E8DFCF] border-2 border-dashed border-[#E8DFCF] bg-[#EF9F27]/10/30">
          <CardContent className="p-6">
            <div className="w-16 h-16 bg-[#EF9F27]/15 rounded-2xl flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-[#EF9F27]" />
            </div>
            <h3 className="text-xl font-bold text-[#412402] mb-3">تعلم بشكل شخصي</h3>
            <p className="text-[#412402]/60 mb-4">
              احصل على اهتمام فردي من معلم متخصص في جلسات خاصة عبر الإنترنت. 
              يمكنك اختيار الوقت المناسب لك والتركيز على المواضيع التي تحتاج مساعدة فيها.
            </p>
            <ul className="space-y-2 text-[#412402]/60">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-500" />
                <span>جلسات فردية مع معلم متخصص</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-500" />
                <span>مرونة في اختيار المواعيد</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-500" />
                <span>تركيز على نقاط الضعف</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-500" />
                <span>متابعة مستمرة للتقدم</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Pricing/Contact Card */}
        <Card className="border-[#E8DFCF]">
          <CardHeader>
            <CardTitle>احجز جلستك الآن</CardTitle>
            <CardDescription>تواصل معنا لحجز جلسة خاصة</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-[#FFFFFF] rounded-lg">
              <h4 className="font-semibold text-[#412402] mb-2">باقات الدروس الخاصة</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#412402]/60">جلسة واحدة (60 دقيقة)</span>
                  <span className="font-semibold">150 ريال</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#412402]/60">4 جلسات شهرياً</span>
                  <span className="font-semibold">500 ريال</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#412402]/60">8 جلسات شهرياً</span>
                  <span className="font-semibold">900 ريال</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-[#412402]/60">
                <Phone className="w-5 h-5 text-[#EF9F27]" />
                <span dir="ltr">+966 50 123 4567</span>
              </div>
              <div className="flex items-center gap-3 text-[#412402]/60">
                <Mail className="w-5 h-5 text-[#EF9F27]" />
                <span>private@manara-exams.com</span>
              </div>
            </div>

            <Button className="w-full bg-[#EF9F27] hover:bg-[#D88E1F]" data-testid="book-private-lesson-btn">
              <MessageCircle className="w-4 h-4 ml-2" />
              تواصل لحجز جلسة
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============== EXAMS SECTION ==============
const ExamsSection = ({ exams, completedExams, handleStartExam }) => {
  const navigate = useNavigate();
  // Group completedExams by exam_id for history display
  const historyByExam = completedExams.reduce((acc, result) => {
    const eid = result.session?.exam_id;
    if (!eid) return acc;
    if (!acc[eid]) acc[eid] = { exam_title: result.exam_title, total_points: result.total_points, attempts: [] };
    acc[eid].attempts.push(result);
    return acc;
  }, {});

  // First attempt per exam (for official score display)
  const firstAttemptByExam = completedExams.reduce((acc, result) => {
    const eid = result.session?.exam_id;
    if (result.is_first_attempt) acc[eid] = result;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#412402]">الاختبارات المتاحة</h1>
        <p className="text-[#412402]/60 mt-1">اختر اختباراً للبدء</p>
      </div>

      {exams.length === 0 ? (
        <Card className="border-[#E8DFCF]">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-[#412402]/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#412402]/80 mb-2">لا توجد اختبارات متاحة</h3>
            <p className="text-[#412402]/50">لا توجد اختبارات متاحة لصفك حالياً</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {exams.map((exam) => {
            const attemptCount = exam.student_attempt_count || 0;
            const maxAttempts = exam.max_attempts || 1;
            const hasInProgress = exam.student_has_in_progress || false;
            const canReattempt = exam.student_can_reattempt !== false && attemptCount < maxAttempts;
            const firstResult = firstAttemptByExam[exam.id];
            const hasCompleted = attemptCount > 0;
            // Latest finished attempt (results arrive sorted by attempt number).
            const lastResult = (historyByExam[exam.id]?.attempts || []).slice(-1)[0];

            return (
              <Card
                key={exam.id}
                className="border-[#E8DFCF] overflow-hidden"
                data-testid={`exam-card-${exam.id}`}
              >
                <div className="border-r-4 border-[#EF9F27]">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold text-[#412402]">{exam.title}</h3>
                          {hasCompleted && !canReattempt && (
                            <Badge className="bg-green-100 text-green-800">مكتمل</Badge>
                          )}
                          {hasCompleted && canReattempt && (
                            <Badge className="bg-blue-100 text-blue-800">
                              {attemptCount}/{maxAttempts} محاولات
                            </Badge>
                          )}
                          {hasInProgress && (
                            <Badge className="bg-amber-100 text-amber-800">جارٍ حالياً</Badge>
                          )}
                        </div>

                        {exam.description && (
                          <p className="text-[#412402]/60 mb-3">{exam.description}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-4 text-sm text-[#412402]/60">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-[#412402]/40" />
                            {exam.duration_minutes} دقيقة
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4 text-[#412402]/40" />
                            {exam.questions?.length || 0} سؤال
                          </span>
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4 text-[#412402]/40" />
                            {exam.total_points} درجة
                          </span>
                          {maxAttempts > 1 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <CheckCircle className="w-4 h-4" />
                              {maxAttempts} محاولات مسموحة
                            </span>
                          )}
                        </div>

                        {/* Official score from first attempt */}
                        {firstResult && firstResult.can_view_results !== false && firstResult.show_score && (
                          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                            <p className="text-xs text-amber-700 mb-1 font-medium">الدرجة الرسمية (المحاولة الأولى)</p>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-lg text-[#412402]">
                                {firstResult.session?.score || 0} / {firstResult.total_points}
                              </span>
                              {firstResult.session?.status === 'terminated' && (
                                <span className="flex items-center gap-1 text-red-600 text-xs">
                                  <AlertTriangle className="w-3 h-3" />
                                  منهي بمخالفة
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {firstResult && firstResult.can_view_results === false && (
                          <div className="mt-4 p-3 bg-[#FAFAFA] rounded-lg border border-[#E8DFCF]">
                            <p className="text-xs text-[#412402]/60">
                              {firstResult.release_mode === 'manual'
                                ? 'لم يتم إصدار النتائج بعد من قبل المعلم'
                                : firstResult.release_mode === 'scheduled' && firstResult.release_datetime
                                  ? `ستتوفر النتائج في: ${new Date(firstResult.release_datetime).toLocaleString('ar-SA')}`
                                  : 'النتائج غير متاحة حالياً'}
                            </p>
                          </div>
                        )}
                      </div>

                      <div className="sm:self-center flex flex-col gap-2">
                        {hasInProgress ? (
                          <Button
                            onClick={() => handleStartExam(exam.id)}
                            className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
                            data-testid={`continue-exam-btn-${exam.id}`}
                          >
                            <Play className="w-4 h-4 ml-2" />
                            استمر في الاختبار
                          </Button>
                        ) : canReattempt ? (
                          <Button
                            onClick={() => handleStartExam(exam.id)}
                            className="w-full sm:w-auto bg-[#EF9F27] hover:bg-[#D88E1F]"
                            data-testid={`start-exam-btn-${exam.id}`}
                          >
                            <Play className="w-4 h-4 ml-2" />
                            {attemptCount === 0 ? 'بدء الاختبار' : 'إعادة المحاولة'}
                          </Button>
                        ) : (
                          <Button variant="outline" disabled className="w-full sm:w-auto">
                            <CheckCircle className="w-4 h-4 ml-2 text-green-600" />
                            تم الإنهاء
                          </Button>
                        )}
                        {lastResult && lastResult.can_view_results !== false && lastResult.session?.id && (
                          <Button
                            variant="outline"
                            onClick={() => navigate(`/student/exam/${exam.id}/results/${lastResult.session.id}`)}
                            className="w-full sm:w-auto border-[#EF9F27] text-[#EF9F27] hover:bg-[#EF9F27]/10"
                            data-testid={`review-exam-btn-${exam.id}`}
                          >
                            <FileText className="w-4 h-4 ml-2" />
                            مراجعة الإجابات
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Previous Attempts History — grouped by exam */}
      {Object.keys(historyByExam).length > 0 && (
        <div className="mt-12">
          <h2 className="text-xl font-bold text-[#412402] mb-4">سجل محاولاتي السابقة</h2>
          <div className="space-y-4">
            {Object.entries(historyByExam).map(([examId, group]) => (
              <Card key={examId} className="border-[#E8DFCF]">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-[#412402] mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#EF9F27]" />
                    {group.exam_title}
                  </h4>
                  <div className="space-y-2">
                    {group.attempts
                      .slice()
                      .sort((a, b) => (a.attempt_number || 1) - (b.attempt_number || 1))
                      .map((result, idx) => (
                        <div
                          key={result.session?.id || idx}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            result.is_first_attempt ? 'bg-amber-50 border border-amber-200' : 'bg-[#FAFAFA]'
                          }`}
                          data-testid={`attempt-row-${result.session?.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-[#412402]/60">
                              المحاولة #{result.attempt_number || idx + 1}
                            </span>
                            {result.is_first_attempt && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                                الدرجة الرسمية
                              </span>
                            )}
                            {result.session?.status === 'terminated' && (
                              <Badge variant="destructive" className="text-xs gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                مخالفة
                              </Badge>
                            )}
                            <span className="text-xs text-[#412402]/40">
                              {new Date(result.session?.ended_at || result.session?.started_at).toLocaleDateString('ar-SA')}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {result.can_view_results === false ? (
                              <div className="text-center">
                                <Clock className="w-5 h-5 text-[#412402]/40 mx-auto mb-0.5" />
                                <p className="text-xs text-[#412402]/50">
                                  {result.release_mode === 'manual' ? 'في انتظار المعلم' : 'غير متاح'}
                                </p>
                              </div>
                            ) : result.show_score ? (
                              <div className="text-left">
                                <p className="text-xl font-bold text-[#EF9F27]">
                                  {result.session?.score || 0}
                                </p>
                                <p className="text-xs text-[#412402]/50">من {result.total_points}</p>
                              </div>
                            ) : (
                              <div className="text-center">
                                <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-0.5" />
                                <p className="text-xs text-[#412402]/50">تم التسليم</p>
                              </div>
                            )}
                            {result.can_view_results !== false && result.session?.id && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-xs border-[#EF9F27] text-[#EF9F27] hover:bg-[#EF9F27]/10 px-2"
                                onClick={() => navigate(`/student/exam/${result.session?.exam_id}/results/${result.session?.id}`)}
                              >
                                مراجعة
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== CONTACT SECTION ==============
const ContactSection = ({ contactForm, setContactForm, handleContactSubmit, sendingMessage }) => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#412402]">تواصل معنا</h1>
        <p className="text-[#412402]/60 mt-1">نحن هنا لمساعدتك</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Contact Form */}
        <Card className="border-[#E8DFCF]">
          <CardHeader>
            <CardTitle>ارسل رسالة</CardTitle>
            <CardDescription>سنرد عليك في أقرب وقت ممكن</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              {/* <div className="space-y-2">
                <Label htmlFor="contact-name">الاسم</Label>
                <Input
                  id="contact-name"
                  placeholder="أدخل اسمك"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  data-testid="contact-name-input"
                />
              </div> */}
              {/* <div className="space-y-2">
                <Label htmlFor="contact-email">البريد الإلكتروني</Label>
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="example@email.com"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  data-testid="contact-email-input"
                />
              </div> */}
              <div className="space-y-2">
                <Label htmlFor="contact-message">الرسالة</Label>
                <Textarea
                  id="contact-message"
                  placeholder="اكتب رسالتك هنا..."
                  rows={4}
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  data-testid="contact-message-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-[#EF9F27] hover:bg-[#D88E1F]"
                disabled={sendingMessage}
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <>
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                    جاري الإرسال...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 ml-2" />
                    إرسال الرسالة
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="space-y-6">
          <Card className="border-[#E8DFCF]">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#412402] mb-4">معلومات التواصل</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#EF9F27]/15 rounded-lg flex items-center justify-center shrink-0">
                    <Phone className="w-5 h-5 text-[#EF9F27]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#412402]">الهاتف</p>
                    <p className="text-[#412402]/60" dir="ltr">0797214964</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  {/* <div className="w-10 h-10 bg-[#EF9F27]/15 rounded-lg flex items-center justify-center shrink-0">
                    <FontFace className="w-5 h-5 text-[#EF9F27]" />
                  </div> */}
                  {/* <div>
                    <p className="font-medium text-[#412402]">البريد الإلكتروني</p>
                    <p className="text-[#412402]/60">support@manara-exams.com</p>
                  </div> */}
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#EF9F27]/15 rounded-lg flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-[#EF9F27]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#412402]">العنوان</p>
                    <p className="text-[#412402]/60"> عمان,المملكة الأردنية الهاشمية</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* <Card className="border-[#E8DFCF] bg-[#EF9F27]/10">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#412402] mb-2">ساعات العمل</h3>
              <div className="space-y-2 text-[#412402]/60">
                <div className="flex justify-between">
                  <span>الأحد - الخميس</span>
                  <span>8:00 ص - 10:00 م</span>
                </div>
                <div className="flex justify-between">
                  <span>الجمعة - السبت</span>
                  <span>2:00 م - 10:00 م</span>
                </div>
              </div>
            </CardContent>
          </Card> */}
        </div>
      </div>
    </div>
  );
};

// ============== TOPICS (QUESTION BANK) SECTION ==============
function buildTopicTree(topics) {
  // root node: { children: {name: node}, topics: [] }
  const root = { children: {}, topics: [] };
  for (const topic of topics) {
    const raw = (topic.category || '').trim();
    const parts = raw ? raw.split('/').map(p => p.trim()).filter(Boolean) : [];
    if (parts.length === 0) {
      root.topics.push(topic);
    } else {
      let node = root;
      for (const part of parts) {
        if (!node.children[part]) node.children[part] = { children: {}, topics: [] };
        node = node.children[part];
      }
      node.topics.push(topic);
    }
  }
  return root;
}

function countTreeTopics(node) {
  return node.topics.length + Object.values(node.children).reduce((s, c) => s + countTreeTopics(c), 0);
}

// First topic anywhere in a subtree — used to read a folder's shared access state
// (price / unlocked / granted) so the folder can render as a single card.
function firstTopicInTree(node) {
  if (node.topics && node.topics.length) return node.topics[0];
  for (const k of Object.keys(node.children)) {
    const t = firstTopicInTree(node.children[k]);
    if (t) return t;
  }
  return null;
}

// A folder shown as a CARD (not a dropdown): name on top, then a lock/open symbol,
// then the buy-price button (locked) or an open/close hint (unlocked).
const FolderCard = ({ name, locked, isOpen, price, granted, onUnlock, onToggle }) => {
  if (locked) {
    return (
      <div
        className="bg-white border border-[#E8DFCF] rounded-2xl shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col items-center text-center"
        data-testid="folder-lock-card"
      >
        <h3 className="font-bold text-[#412402] text-base mb-4 w-full line-clamp-2">{name}</h3>
        <div className="w-16 h-16 rounded-full bg-[#412402]/10 flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-[#412402]/60" />
        </div>
        <Button onClick={onUnlock} className="w-full bg-[#EF9F27] hover:bg-[#D88E1F] text-white" data-testid="unlock-folder-btn">
          <CreditCard className="w-4 h-4 ml-2" />
          {granted ? 'افتح المجلد' : (price > 0 ? `شراء — ${price} د.أ` : 'افتح المجلد')}
        </Button>
        {/* <p className="text-[11px] text-[#412402]/40 mt-2">يُخصم المبلغ من رصيدي مرة واحدة لفتح المجلد كاملاً.</p> */}
      </div>
    );
  }
  return (
    <button
      onClick={onToggle}
      className={`bg-white border rounded-2xl shadow-sm p-5 flex flex-col items-center text-center hover:shadow-md transition-all ${
        isOpen ? 'border-[#EF9F27] ring-1 ring-[#EF9F27]/30' : 'border-[#E8DFCF]'
      }`}
      data-testid="folder-open-card"
    >
      <h3 className="font-bold text-[#412402] text-base mb-4 w-full line-clamp-2">{name}</h3>
      <div className="w-16 h-16 rounded-full bg-[#EF9F27]/15 flex items-center justify-center mb-4">
        {isOpen ? <FolderOpen className="w-8 h-8 text-[#EF9F27]" /> : <Folder className="w-8 h-8 text-[#EF9F27]" />}
      </div>
      <span className="text-xs font-medium text-[#EF9F27] flex items-center gap-1">
        {isOpen ? 'إغلاق المجلد' : 'فتح المجلد'}
        <ChevronLeft className={`w-3.5 h-3.5 transition-transform ${isOpen ? '-rotate-90' : ''}`} />
      </span>
    </button>
  );
};

const TopicTreeNode = ({ node, navigate, depth, defaultLabel, onUnlock }) => {
  // Only ONE folder can be open at this level; opening one hides the other folders.
  const [openFolder, setOpenFolder] = useState(null);
  const [openTopicId, setOpenTopicId] = useState(null);
  const toggleFolder = (name) => setOpenFolder(prev => (prev === name ? null : name));

  const folderNames = Object.keys(node.children).sort((a, b) => a.localeCompare(b, 'ar'));

  // Banks living directly at THIS folder level.
  const realTopics = node.topics.filter((t) => !t.is_folder_placeholder);
  const hasBanksHere = node.topics.length > 0;
  const folderTopic = node.topics[0];
  // Folder is locked if never opened OR re-locked because every bank's tries ran out.
  const thisFolderLocked = hasBanksHere && (!folderTopic.is_unlocked || !!folderTopic.folder_locked);

  // When a folder is open, show only that folder card and hide the siblings.
  const visibleFolderNames = openFolder ? folderNames.filter(n => n === openFolder) : folderNames;

  return (
    <div className={depth === 0 ? 'space-y-6' : 'space-y-4'}>
      {/* Subfolders rendered as a grid of cards (locked = buy card, open = expandable) */}
      {visibleFolderNames.length > 0 && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleFolderNames.map(name => {
            const child = node.children[name];
            const rep = firstTopicInTree(child);
            const isOpen = openFolder === name;
            const locked = !rep || !rep.is_unlocked || !!rep.folder_locked;
            const price = rep?.folder_price ?? rep?.price ?? 0;
            return (
              <FolderCard
                key={name}
                name={name}
                locked={locked}
                isOpen={isOpen}
                price={price}
                granted={!!rep?.granted}
                onToggle={() => toggleFolder(name)}
                onUnlock={() => rep && onUnlock(rep.folder_category || '', rep.teacher_id)}
              />
            );
          })}
        </div>
      )}

      {/* Expanded contents of the single OPEN, unlocked subfolder */}
      {folderNames.map(name => {
        if (openFolder !== name) return null;
        const child = node.children[name];
        const rep = firstTopicInTree(child);
        const locked = !rep || !rep.is_unlocked || !!rep.folder_locked;
        if (locked) return null;
        return (
          <div key={`open-${name}`} className="rounded-2xl border border-[#EF9F27]/30 bg-[#FFFDF7] p-4">
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="w-5 h-5 text-[#EF9F27]" />
              <span className="font-bold text-[#412402]">{name}</span>
            </div>
            <TopicTreeNode node={child} navigate={navigate} depth={depth + 1} defaultLabel={name} onUnlock={onUnlock} />
          </div>
        );
      })}

      {/* Banks directly in THIS folder. Locked → buy card; open → bank cards; empty → note. */}
      {hasBanksHere && (
        thisFolderLocked ? (
          <FolderCard
            name={defaultLabel}
            locked
            price={folderTopic.folder_price ?? folderTopic.price ?? 0}
            granted={!!folderTopic.granted}
            onUnlock={() => onUnlock(folderTopic.folder_category || '', folderTopic.teacher_id)}
          />
        ) : realTopics.length === 0 ? (
          <div className="flex items-center gap-2 px-4 py-6 bg-[#FAFAFA] border border-dashed border-[#E8DFCF] rounded-xl text-sm text-[#412402]/60">
            <Folder className="w-5 h-5 shrink-0 text-[#412402]/30" />
            هذا المجلد مفتوح لكنه لا يحتوي على مواضيع بعد.
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {realTopics.map((topic) => (
              <TopicFolder
                key={topic.id}
                topic={topic}
                isOpen={openTopicId === topic.id}
                onToggle={() => setOpenTopicId(openTopicId === topic.id ? null : topic.id)}
                navigate={navigate}
                onUnlock={onUnlock}
              />
            ))}
          </div>
        )
      )}
    </div>
  );
};

const TopicsSection = ({ topics, navigate, onUnlock }) => {
  const tree = buildTopicTree(topics);
  const unlockedCount = topics.filter(t => t.is_unlocked).length;
  const lockedCount = topics.length - unlockedCount;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-l from-[#EF9F27] to-[#D88E1F] rounded-2xl p-6 text-[#412402]">
        <div className="flex items-center gap-3 mb-2">
          <FolderOpen className="w-7 h-7" />
          <h1 className="text-2xl font-bold">بنك الأسئلة</h1>
        </div>
        {/* <p className="text-[#412402]/90">
          كل مجلد له سعر يحدده المعلم — ادفع ثمنه مرة واحدة لفتحه. الاختبارات تبقى مجانية دائماً.
        </p>
        {topics.length > 0 && (
          <div className="flex items-center gap-3 mt-4 text-xs">
            <Badge className="bg-green-100 text-green-800 gap-1">
              <FolderOpen className="w-3 h-3" /> {unlockedCount} مفتوح
            </Badge>
            {lockedCount > 0 && (
              <Badge className="bg-[#412402]/15 text-[#412402] gap-1">
                <Lock className="w-3 h-3" /> {lockedCount} مقفل
              </Badge>
            )}
          </div>
        )} */}
      </div>

      {topics.length === 0 ? (
        <Card className="border-[#E8DFCF]">
          <CardContent className="py-12 text-center">
            <Folder className="w-12 h-12 text-[#412402]/30 mx-auto mb-3" />
            <p className="text-[#412402]/60">لا توجد مواضيع متاحة لصفك حالياً.</p>
          </CardContent>
        </Card>
      ) : (
        <TopicTreeNode node={tree} navigate={navigate} depth={0} defaultLabel=" باقي المواضيع" onUnlock={onUnlock} />
      )}
    </div>
  );
};

// ============== TOPIC FOLDER CARD (shown only when the folder is unlocked) ====
const TopicFolder = ({ topic, isOpen, onToggle, navigate }) => {
  const hasInProgress = !!topic.in_progress_attempt_id;
  const isReady = topic.is_ready !== false && topic.total_questions > 0;
  const grantedByTeacher = !!topic.granted;
  const hasNew = topic.has_new_questions !== false;
  // New-question rounds flow freely while `hasNew` (until the bank's fresh pool runs
  // out). The per-file `tries` cap governs ONLY re-attempts of OLD questions: when
  // those run out `fileLocked` disables the replay buttons, while any remaining new
  // questions stay available. The folder re-locks only once every file is finished.
  const capLimited = topic.attempts_cap !== null && topic.attempts_cap !== undefined;
  const cap = topic.attempts_cap;
  const fileLocked = !!topic.file_locked;
  const fileRemaining = topic.file_attempts_remaining;

  return (
    <div className="relative" data-testid={`student-topic-${topic.id}`}>
      {grantedByTeacher && (
        <div className="absolute top-0 right-3 z-10 px-2 py-0.5 bg-green-600 text-white text-[10px] font-bold rounded-b-md shadow-sm">
          منحة المعلم
        </div>
      )}
      {hasInProgress && (
        <div className="absolute top-0 left-3 z-0 px-3 py-1 bg-amber-600 text-white text-[10px] font-bold rounded-t-md shadow-sm animate-pulse">
          قيد التنفيذ
        </div>
      )}

      {/* Folder body */}
      <div
        className={`relative bg-gradient-to-br from-[#EF9F27] to-[#D88E1F] p-1 rounded-2xl rounded-tr-none shadow-md hover:shadow-xl transition-all ${isOpen ? 'shadow-xl scale-[1.02]' : ''}`}
      >
        <div className="bg-white rounded-[14px] rounded-tr-none p-5">
          {/* Folder icon header */}
          <button
            onClick={onToggle}
            className="w-full text-right group"
            data-testid={`open-topic-${topic.id}`}
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-[#EF9F27]/15 flex items-center justify-center group-hover:bg-[#EF9F27]/25 transition-colors">
                {isOpen ? (
                  <FolderOpen className="w-7 h-7 text-[#EF9F27]" />
                ) : (
                  <Folder className="w-7 h-7 text-[#EF9F27]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-[#412402] text-base leading-snug line-clamp-2">{topic.title}</h3>
                {topic.description && (
                  <p className="text-xs text-[#412402]/60 mt-1 line-clamp-2">{topic.description}</p>
                )}
              </div>
            </div>
          </button>

          {/* Not-ready state */}
          {!isReady && (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>المعلم لم يضف أسئلة بعد لهذا الموضوع</span>
            </div>
          )}

          {/* Old/new questions status banner */}
          {isReady && (
            <div className="mb-2 space-y-1">
              {hasNew ? (
                <div className="flex items-center gap-1.5 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-1">
                  <Sparkles className="w-3 h-3" /> يوجد أسئلة جديدة لم تُعرض عليك بعد
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                  <AlertCircle className="w-3 h-3" /> انتهت الأسئلة الجديدة — أنت الآن في الأسئلة القديمة
                </div>
              )}
              {capLimited && (
                <div className={`flex items-center gap-1.5 text-[11px] rounded-md px-2 py-1 border ${
                  fileLocked ? 'text-red-700 bg-red-50 border-red-200' : 'text-[#412402]/70 bg-[#FAFAFA] border-[#E8DFCF]'
                }`}>
                  <RefreshCw className="w-3 h-3" />
                  {fileLocked
                    ? `انتهت محاولات إعادة الأسئلة القديمة (${cap}).`
                    : `محاولات إعادة الأسئلة القديمة المتبقية: ${fileRemaining} من ${cap}`}
                </div>
              )}
            </div>
          )}

          {/* Actions (revealed when folder is open OR if in-progress) */}
          {isReady && (isOpen || hasInProgress) && (
            <div className="mt-3 pt-3 border-t border-[#F5EAD3] space-y-2 animate-in fade-in slide-in-from-top-1 duration-200">
              {hasInProgress ? (
                <Button
                  onClick={() => navigate(`/student/topic/${topic.id}/attempt`)}
                  className="w-full bg-amber-700 hover:bg-amber-800"
                  data-testid={`resume-topic-${topic.id}`}
                >
                  <Play className="w-4 h-4 ml-2" /> استكمال المحاولة الجارية
                </Button>
              ) : (
                <>
                  {/* New-question round: available while fresh questions remain,
                      regardless of the re-attempt `tries` cap. */}
                  {hasNew && (
                    <Button
                      onClick={() => navigate(`/student/topic/${topic.id}/attempt`)}
                      className="w-full bg-amber-700 hover:bg-amber-800"
                      data-testid={`start-topic-${topic.id}`}
                    >
                      <Sparkles className="w-4 h-4 ml-2" /> بدء اختبار جديد
                    </Button>
                  )}
                  {(topic.completed_attempts || []).length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs font-semibold text-[#412402]/60 px-1">المحاولات السابقة:</p>
                      {(topic.completed_attempts || []).map((attempt, i) => {
                        const pct = attempt.score !== null && attempt.total_points
                          ? Math.round((attempt.score / attempt.total_points) * 100) : null;
                        const dateStr = attempt.started_at
                          ? new Date(attempt.started_at).toLocaleDateString('ar-SA') : '';
                        return (
                          <div key={attempt.id} className="flex items-center gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/student/topic-attempts/${attempt.id}/results`)}
                              className="flex-1 text-xs border-[#E8DFCF] text-[#412402]/70 hover:bg-[#FAFAFA] justify-start"
                            >
                              <span className="font-medium ml-1">#{i + 1}</span>
                              {dateStr && <span className="text-[10px] text-[#412402]/50 ml-1">{dateStr}</span>}
                              {pct !== null && (
                                <span className={`mr-auto text-xs font-bold ${pct >= 50 ? 'text-green-600' : 'text-red-600'}`}>{pct}%</span>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={fileLocked}
                              onClick={() => navigate(`/student/topic/${topic.id}/attempt?reattempt=${attempt.id}`)}
                              className="shrink-0 text-amber-700 hover:bg-amber-50 px-2 disabled:opacity-40"
                              title={fileLocked ? 'انتهت محاولات إعادة الأسئلة القديمة' : 'إعادة نفس الأسئلة'}
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Hint to open the folder */}
          {isReady && !isOpen && !hasInProgress && (
            <button
              onClick={onToggle}
              className="w-full mt-2 text-xs text-[#EF9F27] hover:text-[#D88E1F] font-medium flex items-center justify-center gap-1"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              افتح المجلد للبدء
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== RECORDED VIDEOS SECTION ==============
function buildVideoTree(videos) {
  const root = { children: {}, items: [] };
  for (const v of videos) {
    const parts = (v.category || '').split('/').map(p => p.trim()).filter(Boolean);
    let node = root;
    for (const part of parts) {
      if (!node.children[part]) node.children[part] = { children: {}, items: [] };
      node = node.children[part];
    }
    node.items.push(v);
  }
  return root;
}
function countVideoTree(node) {
  return node.items.length + Object.values(node.children).reduce((s, c) => s + countVideoTree(c), 0);
}

// First video anywhere in a subtree — used to read a folder's shared access state
// (price / unlocked / granted) so the folder can render as a single card.
function firstVideoInTree(node) {
  if (node.items && node.items.length) return node.items[0];
  for (const k of Object.keys(node.children)) {
    const v = firstVideoInTree(node.children[k]);
    if (v) return v;
  }
  return null;
}

const VideosSection = ({ videos, token, apiUrl, onUnlockFolder }) => {
  const tree = buildVideoTree(videos);
  const [playing, setPlaying] = useState(null); // video being watched
  const unlockedCount = videos.filter(v => v.is_unlocked).length;
  const lockedCount = videos.length - unlockedCount;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-l from-[#EF9F27] to-[#D88E1F] rounded-2xl p-6 text-[#412402]">
        <div className="flex items-center gap-3 mb-2">
          <Video className="w-7 h-7" />
          <h1 className="text-2xl font-bold">الدروس المسجّلة</h1>
        </div>
        {/* <p className="text-[#412402]/90">
          شاهد دروس معلمك المسجّلة. تُفتح المجلدات كاملةً مرة واحدة من رصيدك.
        </p> */}
        {/* {videos.length > 0 && (
          <div className="flex items-center gap-3 mt-4 text-xs">
            <Badge className="bg-green-100 text-green-800 gap-1"><PlayCircleIcon className="w-3 h-3" /> {unlockedCount} مفتوح</Badge>
            {lockedCount > 0 && <Badge className="bg-[#412402]/15 text-[#412402] gap-1"><Lock className="w-3 h-3" /> {lockedCount} مقفل</Badge>}
          </div>
        )} */}
      </div>

      {videos.length === 0 ? (
        <Card className="border-[#E8DFCF]">
          <CardContent className="py-12 text-center">
            <Video className="w-12 h-12 text-[#412402]/30 mx-auto mb-3" />
            <p className="text-[#412402]/60">لا توجد فيديوهات متاحة لصفك حالياً.</p>
          </CardContent>
        </Card>
      ) : (
        <VideoTreeNode node={tree} depth={0} defaultLabel="الفيديوهات" onPlay={setPlaying} onUnlockFolder={onUnlockFolder} />
      )}

      {/* Player dialog */}
      {playing && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPlaying(null)}>
          <div className="bg-black rounded-xl overflow-hidden max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 bg-[#1a0500] text-white">
              <span className="font-medium truncate">{playing.title}</span>
              <button onClick={() => setPlaying(null)} className="text-white/70 hover:text-white">✕</button>
            </div>
            <video
              key={playing.id}
              controls
              autoPlay
              className="w-full max-h-[70vh] bg-black"
              src={`${apiUrl}/api/videos/${playing.id}/stream?token=${encodeURIComponent(token)}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const VideoTreeNode = ({ node, depth, defaultLabel, onPlay, onUnlockFolder }) => {
  // Only ONE folder open at a time; opening one hides the other folders.
  const [openFolder, setOpenFolder] = useState(null);
  const folderNames = Object.keys(node.children).sort((a, b) => a.localeCompare(b, 'ar'));
  const toggleFolder = (name) => setOpenFolder(prev => (prev === name ? null : name));
  const visibleFolderNames = openFolder ? folderNames.filter(n => n === openFolder) : folderNames;

  // All items here share the same folder → same access state.
  const realItems = node.items.filter((v) => !v.is_folder_placeholder);
  const hasItemsHere = node.items.length > 0;
  const folderVideo = node.items[0];
  const thisFolderLocked = hasItemsHere && !folderVideo.is_unlocked;

  return (
    <div className={depth === 0 ? 'space-y-6' : 'space-y-4'}>
      {/* Subfolders as cards (locked = buy card, open = expandable) */}
      {visibleFolderNames.length > 0 && (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {visibleFolderNames.map(name => {
            const child = node.children[name];
            const rep = firstVideoInTree(child);
            const isOpen = openFolder === name;
            const locked = !rep || !rep.is_unlocked;
            const price = rep?.folder_price ?? 0;
            return (
              <FolderCard
                key={name}
                name={name}
                locked={locked}
                isOpen={isOpen}
                price={price}
                granted={!!rep?.granted}
                onToggle={() => toggleFolder(name)}
                onUnlock={() => rep && onUnlockFolder(rep.folder_category || '', rep.teacher_id)}
              />
            );
          })}
        </div>
      )}

      {/* Expanded contents of the single OPEN, unlocked subfolder */}
      {folderNames.map(name => {
        if (openFolder !== name) return null;
        const child = node.children[name];
        const rep = firstVideoInTree(child);
        if (!rep || !rep.is_unlocked) return null;
        return (
          <div key={`open-${name}`} className="rounded-2xl border border-[#EF9F27]/30 bg-[#FFFDF7] p-4">
            <div className="flex items-center gap-2 mb-3">
              <FolderOpen className="w-5 h-5 text-[#EF9F27]" />
              <span className="font-bold text-[#412402]">{name}</span>
            </div>
            <VideoTreeNode node={child} depth={depth + 1} defaultLabel={name} onPlay={onPlay} onUnlockFolder={onUnlockFolder} />
          </div>
        );
      })}

      {/* Videos directly in THIS folder. Locked → buy card; open → video grid; empty → note. */}
      {hasItemsHere && (
        thisFolderLocked ? (
          <FolderCard
            name={defaultLabel}
            locked
            price={folderVideo.folder_price ?? 0}
            granted={!!folderVideo.granted}
            onUnlock={() => onUnlockFolder(folderVideo.folder_category || '', folderVideo.teacher_id)}
          />
        ) : realItems.length === 0 ? (
          <div className="flex items-center gap-2 px-4 py-6 bg-[#FAFAFA] border border-dashed border-[#E8DFCF] rounded-xl text-sm text-[#412402]/60">
            <Folder className="w-5 h-5 shrink-0 text-[#412402]/30" />
            هذا المجلد مفتوح لكنه لا يحتوي على فيديوهات بعد.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {realItems.map(v => (
              <VideoCard key={v.id} video={v} onPlay={onPlay} />
            ))}
          </div>
        )
      )}
    </div>
  );
};

const VideoCard = ({ video, onPlay }) => (
  <div className="relative bg-white border border-[#E8DFCF] rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid={`student-video-${video.id}`}>
    <div className="relative h-32 bg-gradient-to-br from-[#412402] to-[#6B4C2A] flex items-center justify-center">
      <button onClick={() => onPlay(video)} className="w-14 h-14 rounded-full bg-white/90 hover:bg-white flex items-center justify-center group" data-testid={`play-video-${video.id}`}>
        <PlayCircleIcon className="w-9 h-9 text-[#EF9F27] group-hover:scale-110 transition-transform" />
      </button>
    </div>
    <div className="p-4">
      <h3 className="font-bold text-[#412402] text-sm leading-snug line-clamp-2">{video.title}</h3>
      {video.description && <p className="text-xs text-[#412402]/60 mt-1 line-clamp-2">{video.description}</p>}
      <Button onClick={() => onPlay(video)} className="w-full mt-3 bg-[#EF9F27] hover:bg-[#D88E1F] text-white">
        <PlayCircleIcon className="w-4 h-4 ml-2" /> مشاهدة
      </Button>
    </div>
  </div>
);

export default StudentExamList;
